import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import AdminTables from './adminTables';
import createAxiosInstance from '../../utils/axiosConfig';
import { delete_file, ingested_files, ingest_file_multiple_new, nist_frameworks } from '../../utils/endpoints';
import Alert from './alerts/alert';
import '../../assets/scss/sidebar.css';
import '../../assets/scss/app.css';
import DeleteIcon from '../../assets/svg/deleteIcon';
import { getUseCaseName, useCases } from '../../utils/constants';
import { FrameworkCategory, UseCase } from '../../utils/interfaces';
import NextTabIcon from '../../assets/svg/nextTabIcon';
import TickIcon from '../../assets/svg/tickIcon';

interface IngestedFilesSidebarProps {
  isOpen: boolean;
  onClose: () => void;
  onSwitchToUpload: () => void;
}

interface FileData {
  id: number;
  name: string;
  size: string;
  uploadDate: string;
  type: string;
}

// Simple Dropdown Portal Component
interface DropdownPortalProps {
  isOpen: boolean;
  onClose: () => void;
  triggerRef: React.RefObject<HTMLElement>;
  children: React.ReactNode;
  style?: React.CSSProperties;
}

const DropdownPortal: React.FC<DropdownPortalProps> = ({ isOpen, onClose, triggerRef, children, style }) => {
  const [position, setPosition] = useState({ top: 0, left: 0, width: 0 });
  const [isPositioned, setIsPositioned] = useState(false);

  useEffect(() => {
    if (isOpen && triggerRef.current && !isPositioned) {
      // Use requestAnimationFrame to ensure DOM is ready
      requestAnimationFrame(() => {
        if (triggerRef.current) {
          const rect = triggerRef.current.getBoundingClientRect();
          setPosition({
            top: rect.bottom + window.scrollY + 2,
            left: rect.left + window.scrollX,
            width: rect.width
          });
          setIsPositioned(true);
        }
      });
    }
    
    // Reset positioning when dropdown closes
    if (!isOpen) {
      setIsPositioned(false);
    }
  }, [isOpen, triggerRef, isPositioned]);

  useEffect(() => {
    if (isOpen) {
      const handleClickOutside = (event: MouseEvent) => {
        if (triggerRef.current && !triggerRef.current.contains(event.target as Node)) {
          const dropdownElement = document.getElementById('dropdown-portal');
          if (dropdownElement && !dropdownElement.contains(event.target as Node)) {
            onClose();
          }
        }
      };

      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen, onClose, triggerRef]);

  // Don't render until positioned to prevent flickering
  if (!isOpen || !isPositioned) return null;

  return createPortal(
    <div
      id="dropdown-portal"
      style={{
        position: 'fixed',
        top: position.top,
        left: position.left,
        width: Math.max(position.width, 250),
        backgroundColor: 'white',
        border: '1px solid #dee2e6',
        borderRadius: '0.375rem',
        boxShadow: '0 0.5rem 1rem rgba(0, 0, 0, 0.15)',
        zIndex: 10000,
        maxHeight: '250px',
        overflowY: 'auto',
        fontSize: '12px',
        opacity: isPositioned ? 1 : 0,
        transition: 'opacity 0.1s ease-in-out',
        ...style
      }}
      onClick={(e) => e.stopPropagation()}
    >
      {children}
    </div>,
    document.body
  );
};

const IngestedFilesSidebar: React.FC<IngestedFilesSidebarProps> = ({ isOpen, onClose, onSwitchToUpload }) => {
  const [fileList, setFileList] = useState<string[]>([]);
  const [uploadedFiles, setUploadedFiles] = useState<string[]>([]);
  const [pendingFiles, setPendingFiles] = useState<string[]>([]);
  const [selectedFiles, setSelectedFiles] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [ingestLoading, setIngestLoading] = useState(false);
  const [alert, setAlert] = useState<{ message: string; type: 'success' | 'danger' | 'warning'; show: boolean } | null>(null);
  const [selectedFilter, setSelectedFilter] = useState<string>('ALL');
  const [searchTerm, setSearchTerm] = useState<string>('');

  // Add ingestion tracking states similar to ingestData.tsx
  const [ingestions, setIngestions] = useState<any[]>([]);

  // Fetch ingestion data from localStorage
  const fetchIngestionData = () => {
    const storedData = localStorage.getItem("ingestionData");
    if (storedData) {
      const parsedData = JSON.parse(storedData);
      const filteredData = parsedData.filter(
        (item: { timestamp: number }) =>
          Date.now() - item.timestamp <= 48 * 60 * 60 * 1000
      );
      setIngestions(filteredData);
    }
  };

  // Check status function similar to ingestData.tsx
  const checkStatus = async (process_id: string) => {
    try {
      const axiosInstance = createAxiosInstance();
      const response = await axiosInstance.get("/ingest/check_process/" + process_id);
      const status = response?.data?.status.toLowerCase();
      
      if (response?.data?.status === "SUCCESS") {
        const storedData = localStorage.getItem("ingestionData");
        if (storedData) {
          const parsedData = JSON.parse(storedData);
          const matchedIngestion = parsedData.find(
            (item: { process_id: string }) => item.process_id === process_id
          );

          if (matchedIngestion) {
            setAlert({
              message: `Ingestion for ${getUseCaseName(matchedIngestion.use_case)} completed successfully!`,
              type: 'success',
              show: true
            });

            // Remove completed ingestion from localStorage
            const updatedData = parsedData.filter(
              (item: { process_id: string }) => item.process_id !== process_id
            );
            localStorage.setItem("ingestionData", JSON.stringify(updatedData));
            setIngestions(updatedData);

            // Refresh the ingested files list
            getFilesList();
          }
        }
      } else if (response?.data?.status !== "PENDING") {
        // Status is failure
        const storedData = localStorage.getItem("ingestionData");
        if (storedData) {
          const parsedData = JSON.parse(storedData);
          const matchedIngestion = parsedData.find(
            (item: { process_id: string }) => item.process_id === process_id
          );

          if (matchedIngestion) {
            setAlert({
              message: `Ingestion for ${getUseCaseName(matchedIngestion.use_case)} failed.`,
              type: 'danger',
              show: true
            });

            // Remove failed ingestion from localStorage
            const updatedData = parsedData.filter(
              (item: { process_id: string }) => item.process_id !== process_id
            );
            localStorage.setItem("ingestionData", JSON.stringify(updatedData));
            setIngestions(updatedData);
          }
        }
      } else {
        // Status is pending
        setAlert({
          message: 'Ingestion is still pending...',
          type: 'warning',
          show: true
        });
      }
    } catch (error) {
      console.error("Error checking ingestion status:", error);
      setAlert({
        message: 'Error checking ingestion status.',
        type: 'danger',
        show: true
      });

      // Remove the ingestion from the list on error
      const storedData = localStorage.getItem("ingestionData");
      if (storedData) {
        const parsedData = JSON.parse(storedData);
        const updatedData = parsedData.filter(
          (item: { process_id: string }) => item.process_id !== process_id
        );
        localStorage.setItem("ingestionData", JSON.stringify(updatedData));
        setIngestions(updatedData);
      }
    }
  };

  // Form state from ingestData.tsx
  const [listFiles, setListFiles] = useState<any[]>([]);
  const [errors, setErrors] = useState<any>({});
  const [frameworks, setFrameworks] = useState<FrameworkCategory[]>([]);
  const [formData, setFormData] = useState({
    use_case: [] as string[],
    base_path: "",
    version: "",
    frameworks: [] as string[],
    custom_topics: "",
    requires_enrichment: false,
  });

  // Dropdown refs and state
  const useCaseDropdownRef = React.useRef<HTMLDivElement>(null);
  const frameworkDropdownRef = React.useRef<HTMLDivElement>(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isFrameworkDropdownOpen, setIsFrameworkDropdownOpen] = useState(false);
  
  // Use case edit dropdown state
  const [useCaseEditDropdown, setUseCaseEditDropdown] = useState<{[key: string]: boolean}>({});
  const useCaseEditDropdownRefs = React.useRef<{[key: string]: React.RefObject<HTMLDivElement>}>({});

  // Form validation function from ingestData.tsx
  const validate = (formData: any, setErrors: (errors: any) => void) => {
    const tempErrors: any = {};

    Object.keys(formData).forEach((key) => {
      // Skip base_path validation since it's set programmatically from selected files
      if (key === 'base_path') return;
      
      if (!formData[key as keyof typeof formData]) {
        if (
          formData?.requires_enrichment == false ||
          formData?.requires_enrichment == true
        ) {
          return;
        } else {
          tempErrors[key] = `${key} is required`;
        }
      }

      if (key === 'custom_topics' && formData[key]) {
        const topics = formData[key].split(',');
        const hasEmptyValues = topics.some((topic: string) => topic.trim() === '');
        if (hasEmptyValues) {
          tempErrors[key] = 'Invalid format. Please use comma-separated values without empty entries';
        }
      }
    });

    setErrors(tempErrors);
    return Object.keys(tempErrors).length === 0;
  };

  // Form handlers from ingestData.tsx
  const handleUseCaseChange = (
    e: React.MouseEvent | React.ChangeEvent,
    selectedValue: string
  ) => {
    e.stopPropagation();
    const currentSelections = [...formData.use_case];
    const index = currentSelections.indexOf(selectedValue);

    if (index === -1) {
      currentSelections.push(selectedValue);
    } else {
      currentSelections.splice(index, 1);
    }

    setFormData({ ...formData, use_case: currentSelections });
    validate({ use_case: currentSelections }, setErrors);
  };

  const handleFrameworkChange = (
    e: React.MouseEvent | React.ChangeEvent,
    selectedValue: string
  ) => {
    e.stopPropagation();
    const currentSelections = [...formData.frameworks];
    const index = currentSelections.indexOf(selectedValue);
    if (index === -1) {
      currentSelections.push(selectedValue);
    } else {
      currentSelections.splice(index, 1);
    }

    setFormData({ ...formData, frameworks: currentSelections });
  };

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    validate({ [name]: value }, setErrors);
  };

  // Form submit handler from ingestData.tsx
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate(formData, setErrors)) return;

    // Check if files are selected for bulk ingestion
    if (selectedFiles.length === 0) {
      setAlert({
        message: 'Please select files to ingest.',
        type: 'warning',
        show: true
      });
      return;
    }

    // Use the first selected file as base_path for bulk ingestion
    const basePath = selectedFiles[0];

    const payload = {
      use_case_list: formData.use_case.map((uc) => `use_case_${uc}`),
      base_path: basePath,
      requires_enrichment: formData.requires_enrichment,
      custom_topics: formData.custom_topics,
      version: formData.version,
      framework_list: formData.frameworks.join(','),
    };

    try {
      setIngestLoading(true);
      const axiosInstance = createAxiosInstance();
      const response = await axiosInstance.post(ingest_file_multiple_new, payload);

      // Check for top-level errors first
      if (response.data?.error) {
        setAlert({
          message: response?.data?.error,
          type: 'danger',
          show: true
        });
      } else if (response?.data?.error_message) {
        setAlert({
          message: response?.data?.error_message,
          type: 'danger',
          show: true
        });
      } else if (response.data?.response_list) {
        // Check for failures in response_list
        const failures = response.data.response_list.filter((item: any) => 
          item.process_status === 'FAILURE'
        );
        
        if (failures.length > 0) {
          // Collect all error messages
          const errorMessages = failures.map((failure: any) => failure.error_message).join('; ');
          setAlert({
            message: `${errorMessages}`,
            type: 'danger',
            show: true
          });
          // Reset form and clear selected files
          setFormData({
            use_case: [],
            base_path: "",
            version: "",
            frameworks: [],
            custom_topics: "",
            requires_enrichment: false,
          });
          setSelectedFiles([]);
          
                 } else {
           // All processes succeeded - store in localStorage for status tracking
           const newIngestions: any[] = [];
           
           response.data.response_list.forEach((item: any) => {
             if (item.process_status.toLowerCase() !== "failure") {
               // Extract filename properly from base_path
               let fileName = "Unknown";
               if (basePath) {
                 if (basePath.includes("/data/")) {
                   // Try the original logic first
                   fileName = basePath.split("/data/")[1] || basePath;
                 } else {
                   // If no "/data/" found, get the last part of the path
                   fileName = basePath.split('/').pop() || basePath;
                 }
               }
               
               const ingestionItem = {
                 name: fileName,
                 use_case: item.use_case,
                 process_id: item.process_id,
                 status: item.process_status.toLowerCase(),
                 timestamp: Date.now(),
               };
               newIngestions.push(ingestionItem);
             }
           });

           if (newIngestions.length > 0) {
             const existingIngestions = ingestions;
             const updatedIngestions = [...existingIngestions, ...newIngestions];
             setIngestions(updatedIngestions);
             localStorage.setItem("ingestionData", JSON.stringify(updatedIngestions));
           }

           setAlert({
             message: `Bulk ingestion started successfully for ${selectedFiles.length} file(s)!`,
             type: 'success',
             show: true
           });
           
           // Reset form and clear selected files
           setFormData({
             use_case: [],
             base_path: "",
             version: "",
             frameworks: [],
             custom_topics: "",
             requires_enrichment: false,
           });
           setSelectedFiles([]);
           
           // Refresh lists
           getFilesList();
           getUploadedFilesList();
         }
      } else {
        setAlert({
          message: `Files ingested successfully for ${selectedFiles.length} file(s)!`,
          type: 'success',
          show: true
        });
        
        // Reset form and clear selected files
        setFormData({
          use_case: [],
          base_path: "",
          version: "",
          frameworks: [],
          custom_topics: "",
          requires_enrichment: false,
        });
        setSelectedFiles([]);
        
        // Refresh lists
        getFilesList();
        getUploadedFilesList();
      }
    } catch (err) {
      setAlert({
        message: 'Some errors occurred. Please try again later.',
        type: 'danger',
        show: true
      });
      console.log(err);
    } finally {
      setIngestLoading(false);
    }
  };

  // Fetch all files list (uploaded files)
  const getUploadedFilesList = useCallback(async () => {
    try {
      const axiosInstance = createAxiosInstance();
      const response = await axiosInstance.get('/ingest/list_files');
      setUploadedFiles(response?.data?.files || []);
      setListFiles(response?.data?.files || []);
    } catch (error) {
      console.error('Error fetching uploaded files:', error);
    }
  }, []);

  // Fetch ingested files list
  const getFilesList = useCallback(async () => {
    try {
      setLoading(true);
      const axiosInstance = createAxiosInstance();
      
      // Try to fetch files from all known use cases
      const allFiles: any[] = [];
      const knownUseCases = [1, 2, 3, 7]; // Only fetch from existing use cases
      
      // Fetch files from each known use case
      // for (const useCaseNum of knownUseCases) {
        try {
          const response = await axiosInstance.get(`/ingest/all_ingested_files_with_metadata`);
          const files = response?.data?.ingested_files || [];
          if (files.length > 0) {
            allFiles.push(...files);
          }
        } catch (useCaseError) {
          console.warn(`No files found, or endpoint error:`, useCaseError);
          // Continue to next use case even if one fails
        }
      // }
      
      setFileList(allFiles);
    } catch (error) {
      console.error('Error fetching files:', error);
      setAlert({
        message: 'Error loading files. Please try again.',
        type: 'danger',
        show: true
      });
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch frameworks from ingestData.tsx
  const getFrameworks = async () => {
    try {
      const axiosInstance = createAxiosInstance();
      const response = await axiosInstance.get(nist_frameworks);
      setFrameworks(response?.data.sort((a: FrameworkCategory) => a.is_enabled ? -1 : 1));
    } catch (e: any) {
      console.log(e, 'Error');
    }
  };

  // Calculate pending files (uploaded but not ingested)
  useEffect(() => {
    console.log(uploadedFiles, fileList, 'uploadedFiles, fileList');
    
    // Extract document names from fileList (which contains objects with document_name property)
    const ingestedFileNames = fileList.map((file: any) => file.document_name);
    
    // Filter uploaded files that are not in the ingested files list
    const pending = uploadedFiles.filter(file => {
      const fileName = file.split('/').pop(); // Get filename from full path
      return !ingestedFileNames.includes(fileName);
    });
    
    setPendingFiles(pending);
  }, [uploadedFiles, fileList]);

  // Handle file selection
  const handleFileSelect = (fileName: string) => {
    setSelectedFiles(prev => {
      if (prev.includes(fileName)) {
        return prev.filter(f => f !== fileName);
      } else {
        return [...prev, fileName];
      }
    });
  };

  // Select all files
  const handleSelectAll = () => {
    if (selectedFiles.length === pendingFiles.length) {
      setSelectedFiles([]);
    } else {
      setSelectedFiles([...pendingFiles]);
    }
  };

  // Ingest selected files (individual file ingestion)
  const handleIngestFiles = async () => {
    if (selectedFiles.length === 0) {
      setAlert({
        message: 'Please select files to ingest.',
        type: 'warning',
        show: true
      });
      return;
    }

    try {
      setIngestLoading(true);
      const axiosInstance = createAxiosInstance();
      
      for (const fileName of selectedFiles) {
        await axiosInstance.post('/ingest/file', {
          file_name: fileName
        });
      }

      setAlert({
        message: `${selectedFiles.length} file(s) ingested successfully!`,
        type: 'success',
        show: true
      });

      setSelectedFiles([]);
      // Refresh both lists
      getFilesList();
      getUploadedFilesList();
    } catch (error) {
      console.error('Error ingesting files:', error);
      setAlert({
        message: 'Error ingesting files. Please try again.',
        type: 'danger',
        show: true
      });
    } finally {
      setIngestLoading(false);
    }
  };

  // Delete uploaded file
  const handleDeleteUploadedFile = async (fileName: string) => {
    if (!window.confirm(`Are you sure you want to delete ${fileName}?`)) {
      return;
    }

    try {
      const axiosInstance = createAxiosInstance();
      const response = await axiosInstance.post(delete_file, {
        file_name: fileName
      });

      if (response.data?.error) {
        setAlert({
          message: response.data.error,
          type: 'danger',
          show: true
        });
      } else {
        setAlert({
          message: `${fileName} deleted successfully!`,
          type: 'success',
          show: true
        });
        getUploadedFilesList(); // Refresh the uploaded files list
        setSelectedFiles(prev => prev.filter(f => f !== fileName)); // Remove from selection
      }
    } catch (error) {
      console.error('Error deleting file:', error);
      setAlert({
        message: `Error deleting ${fileName}. Please try again.`,
        type: 'danger',
        show: true
      });
    }
  };

  // Load files when sidebar opens
  useEffect(() => {
    if (isOpen) {
      getFilesList();
      getUploadedFilesList();
      getFrameworks();
      fetchIngestionData(); // Load ingestion data from localStorage
    }
  }, [isOpen, getFilesList, getUploadedFilesList]);



  // Helper function to check if a file is currently being processed
  const isFileBeingProcessed = (fileName: string) => {
    return ingestions.some(ingestion => {
      // Try exact match first
      if (ingestion.name === fileName) return true;
      
      // Try matching just the filename without path
      const ingestionFileName = ingestion.name.split('/').pop() || ingestion.name;
      const currentFileName = fileName.split('/').pop() || fileName;
      
      return ingestionFileName === currentFileName && 
             (ingestion.status === 'pending' || ingestion.status === 'success');
    });
  };

  // Helper function to get process ID for a file
  const getFileProcessId = (fileName: string) => {
    const ingestion = ingestions.find(ing => {
      // Try exact match first
      if (ing.name === fileName) return true;
      
      // Try matching just the filename without path
      const ingestionFileName = ing.name.split('/').pop() || ing.name;
      const currentFileName = fileName.split('/').pop() || fileName;
      
      return ingestionFileName === currentFileName;
    });
    return ingestion?.process_id;
  };

  // Filter button click handler
  const handleFilterClick = (filterType: string) => {
    setSelectedFilter(filterType);
  };

  // Search input handler
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  // File table configuration with filtering
  const getFileTableData = () => {
    let filteredFiles = fileList.map((item: any, index) => ({
      id: index,
      name: item.document_name,
      size: 'N/A', // Size not available from API
      uploadDate: 'N/A', // Date not available from API
      type: item.document_name.split('.').pop()?.toUpperCase() || 'Unknown',
      use_cases: item.use_cases,
      framework: item.framework,
      version: item.version,
      custom_topics: item.custom_topics,
      requires_enrichment: item.requires_enrichment,
      ingested_at: item.ingested_at,
      ingested_by: item.ingested_by,
    }));

    // Add files that are currently being processed from localStorage to the top of the list
    const ingestedFileNames = filteredFiles.map(file => file.name);
    
    ingestions.forEach((ingestion, index) => {
      const ingestionFileName = ingestion.name.split('/').pop() || ingestion.name;
      
      // Check if this file is already in the list (with flexible matching)
      const isAlreadyInList = ingestedFileNames.some(fileName => {
        const apiFileName = fileName.split('/').pop() || fileName;
        return apiFileName === ingestionFileName;
      });
      
      // Only add if it's not already in the list and is being processed
      if (!isAlreadyInList && (ingestion.status === 'pending' || ingestion.status === 'success')) {
        
        filteredFiles.unshift({ // Add to the beginning of the array (top of list)
          id: -index - 1, // Negative ID to avoid conflicts
          name: ingestionFileName, // Use clean filename for consistency
          size: 'Processing...',
          uploadDate: 'Processing...',
          type: ingestionFileName.split('.').pop()?.toUpperCase() || 'Unknown',
          use_cases: [ingestion.use_case],
          framework: 'Processing...',
          version: 'Processing...',
          custom_topics: 'Processing...',
          requires_enrichment: false,
          ingested_at: 'Processing...',
          ingested_by: 'Processing...',
        });
      }
    });

    // Apply file type filter
    if (selectedFilter !== 'ALL') {
      filteredFiles = filteredFiles.filter(file => {
        const fileType = file.type.toUpperCase();
        switch (selectedFilter) {
          case 'CSV':
            return fileType === 'CSV';
          case 'XLSX':
            return fileType === 'XLSX' || fileType === 'XLS';
          case 'WORD':
            return fileType === 'DOCX' || fileType === 'DOC';
          case 'PDF':
            return fileType === 'PDF';
          default:
            return true;
        }
      });
    }

    // Apply search filter
    if (searchTerm.trim()) {
      filteredFiles = filteredFiles.filter(file =>
        file.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    return filteredFiles;
  };

  // Ensure table updates when ingestions state changes
  const tableData = useMemo(() => getFileTableData(), [fileList, ingestions, selectedFilter, searchTerm]);

  const handleDownloadFile = async (fileName: string) => {
    try {
      const axiosInstance = createAxiosInstance();
      const response = await axiosInstance.get(`/ingest/download_file/${fileName}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading file:', error);
      setAlert({
        message: `Error downloading ${fileName}. Please try again.`,
        type: 'danger',
        show: true
      });
    }
  };

  const handleDeleteFile = async (fileName: string) => {
    if (!window.confirm(`Are you sure you want to delete ${fileName}?`)) {
      return;
    }

    // Get the process_id for this file (if it's a processing file)
    const processId = getFileProcessId(fileName);
    
    if (processId) {
      // Remove from localStorage instead of API call
      try {
        // Remove by process_id directly
        const updatedIngestions = ingestions.filter(ingestion => 
          ingestion.process_id !== processId
        );
        setIngestions(updatedIngestions);
        localStorage.setItem("ingestionData", JSON.stringify(updatedIngestions));
        
        setAlert({
          message: `${fileName} removed from processing queue!`,
          type: 'success',
          show: true
        });
      } catch (error) {
        console.error('Error removing from localStorage:', error);
        setAlert({
          message: `Error removing ${fileName} from processing queue.`,
          type: 'danger',
          show: true
        });
      }
    } else {
      // Normal API delete for fully ingested files
      try {
        const axiosInstance = createAxiosInstance();
        const response = await axiosInstance.post(delete_file, {
          file_name: fileName
        });

        if (response.data?.error) {
          setAlert({
            message: response.data.error,
            type: 'danger',
            show: true
          });
        } else {
          setAlert({
            message: `${fileName} deleted successfully!`,
            type: 'success',
            show: true
          });
          getFilesList(); // Refresh the list
        }
      } catch (error) {
        console.error('Error deleting file:', error);
        setAlert({
          message: `Error deleting ${fileName}. Please try again.`,
          type: 'danger',
          show: true
        });
      }
    }
  };

  // Use case edit handlers
  const handleUseCaseEditToggle = (fileName: string) => {
    setUseCaseEditDropdown(prev => ({
      ...prev,
      [fileName]: !prev[fileName]
    }));
  };

  const handleUseCaseEditClose = (fileName: string) => {
    setUseCaseEditDropdown(prev => ({
      ...prev,
      [fileName]: false
    }));
  };

  const handleUseCaseUpdate = async (fileName: string, selectedUseCases: string[]) => {
    try {
      setIngestLoading(true);
      
      console.log('Debug: Starting use case update for file:', fileName);
      console.log('Debug: Selected use cases:', selectedUseCases);
      console.log('Debug: Current fileList length:', fileList.length);
      console.log('Debug: Current uploadedFiles length:', uploadedFiles.length);
      
      // Find the file in the fileList to get its existing metadata
      const fileData = fileList.find((file: any) => file.document_name === fileName);
      
      console.log('Debug: Found fileData:', fileData);
      
      if (!fileData) {
        console.error('Debug: File data not found in fileList');
        throw new Error(`File "${fileName}" not found in ingested files list`);
      }
      
      // Type the fileData as any to access properties
      const typedFileData: any = fileData;
      
      // Try to find the file path in multiple ways
      let filePath = null;
      
      // First, try to find in uploaded files
      filePath = uploadedFiles.find(uploadedFile => {
        const uploadedFileName = uploadedFile.split('/').pop();
        return uploadedFileName === fileName;
      });
      
      console.log('Debug: Found file path in uploadedFiles:', filePath);
      
      // If not found in uploaded files, construct a path based on the file name
      if (!filePath) {
        // Try common path patterns
        filePath = `/data/${fileName}`;
        console.log('Debug: File not found in uploadedFiles, using constructed path:', filePath);
      }
      
      // Prepare the payload with existing metadata and new use cases
      const payload = {
        use_case_list: selectedUseCases,
        base_path: filePath,
        requires_enrichment: typedFileData.requires_enrichment || false,
        custom_topics: typedFileData.custom_topics || "",
        version: typedFileData.version[0] || "",
        framework_list: typedFileData.framework || "",
      };
      
      console.log('Debug: Payload prepared:', payload);
      
      const axiosInstance = createAxiosInstance();
      console.log('Debug: Making API call to re-ingest file...');
      
      const response = await axiosInstance.post(ingest_file_multiple_new, payload);
      
      console.log('Debug: API response received:', response.data);
      
      // Handle response similar to bulk ingestion
      if (response.data?.error) {
        setAlert({
          message: response?.data?.error,
          type: 'danger',
          show: true
        });
      } else if (response?.data?.error_message) {
        setAlert({
          message: response?.data?.error_message,
          type: 'danger',
          show: true
        });
      } else if (response.data?.response_list) {
        const failures = response.data.response_list.filter((item: any) => 
          item.process_status === 'FAILURE'
        );
        
        if (failures.length > 0) {
          const errorMessages = failures.map((failure: any) => failure.error_message).join('; ');
          setAlert({
            message: `Re-ingestion failed: ${errorMessages}`,
            type: 'danger',
            show: true
          });
        } else {
          // Success - store new ingestions in localStorage for tracking
          const newIngestions: any[] = [];
          
          response.data.response_list.forEach((item: any) => {
            if (item.process_status.toLowerCase() !== "failure") {
              const ingestionItem = {
                name: fileName,
                use_case: item.use_case,
                process_id: item.process_id,
                status: item.process_status.toLowerCase(),
                timestamp: Date.now(),
              };
              newIngestions.push(ingestionItem);
            }
          });

          if (newIngestions.length > 0) {
            const existingIngestions = ingestions;
            const updatedIngestions = [...existingIngestions, ...newIngestions];
            setIngestions(updatedIngestions);
            localStorage.setItem("ingestionData", JSON.stringify(updatedIngestions));
          }
          
          setAlert({
            message: `File "${fileName}" re-ingestion started successfully!`,
            type: 'success',
            show: true
          });
        }
      } else {
        setAlert({
          message: `File "${fileName}" re-ingested successfully!`,
          type: 'success',
          show: true
        });
      }
      
      // Refresh the file list and close the dropdown
      getFilesList();
      handleUseCaseEditClose(fileName);
      
    } catch (error: any) {
      console.error('Error updating use cases:', error);
      console.error('Error stack:', error.stack);
      setAlert({
        message: error.message || `Failed to re-ingest file "${fileName}"`,
        type: 'danger',
        show: true
      });
    } finally {
      setIngestLoading(false);
    }
  };

  // Use Case Edit Dropdown Component
  const UseCaseEditDropdown = ({ fileName, currentUseCases }: { fileName: string, currentUseCases: string[] }) => {
    const [selectedUseCases, setSelectedUseCases] = useState<string[]>(currentUseCases);
    
    // Create or get ref for this dropdown
    if (!useCaseEditDropdownRefs.current[fileName]) {
      useCaseEditDropdownRefs.current[fileName] = React.createRef<HTMLDivElement>();
    }
    
    const dropdownRef = useCaseEditDropdownRefs.current[fileName];

    const handleUseCaseToggle = (useCaseValue: string) => {
      setSelectedUseCases(prev => {
        if (prev.includes(useCaseValue)) {
          return prev.filter(uc => uc !== useCaseValue);
        } else {
          return [...prev, useCaseValue];
        }
      });
    };

    const handleSave = () => {
      handleUseCaseUpdate(fileName, selectedUseCases);
    };

    return (
      <DropdownPortal
        isOpen={useCaseEditDropdown[fileName] || false}
        onClose={() => handleUseCaseEditClose(fileName)}
        triggerRef={dropdownRef}
        style={{
            borderRadius: '1.875rem',
            border: '1px solid #dee2e6',
            boxShadow: '0 0.5rem 1rem rgba(0, 0, 0, 0.15)',
            zIndex: 10000,
            maxHeight: '250px',
            overflowY: 'auto',
            fontSize: '12px',
            width: '350px'
        }}
      >
        <div style={{ padding: '12px' }}>
            {useCases.map((useCase) => {
             const useCaseValue = `use_case_${useCase.value}`;
             const isSelected = selectedUseCases.includes(useCaseValue);
             
             return (
               <div
                 key={useCase.value}
                 style={{
                   display: 'flex',
                   justifyContent: 'space-between',
                   alignItems: 'center',
                   padding: '6px 8px',
                   marginBottom: '8px',
                   cursor: 'pointer',
                   fontSize: '0.875rem',
                   fontFamily: 'Roboto',
                   backgroundColor: isSelected ? '#D9D9D9' : 'transparent',
                   borderRadius: '1.875rem',
                   transition: 'background-color 0.2s ease'
                 }}
                 onClick={() => handleUseCaseToggle(useCaseValue)}
                 onMouseEnter={(e) => {
                   e.currentTarget.style.backgroundColor = '#D9D9D9';
                 }}
                 onMouseLeave={(e) => {
                   e.currentTarget.style.backgroundColor = isSelected ? '#D9D9D9' : 'transparent';
                 }}
               >
                 <span style={{ fontSize: '0.875rem' }}>{useCase.name}</span>
                 <div
                   style={{
                     width: '1.625rem',
                     height: '1.625rem',
                     borderRadius: '50%',
                     backgroundColor: 'white',
                     border: '1px solid #dee2e6',
                     display: 'flex',
                     alignItems: 'center',
                     justifyContent: 'center',
                     marginRight: '8px',
                     opacity: isSelected ? 1 : 0.3
                   }}
                 >
                    <TickIcon />
                   {/* <i 
                     className="bi bi-check" 
                     style={{ 
                       color: '#183FCE', 
                       fontSize: '1.5rem',
                       fontWeight: 'bold'
                     }}
                   ></i> */}
                 </div>
               </div>
             );
           })}
          <div style={{ 
            marginTop: '12px', 
            paddingTop: '8px', 
            borderTop: '1px solid #dee2e6',
            display: 'flex',
            gap: '8px',
            justifyContent: 'flex-end'
          }}>
            <button
              onClick={() => handleUseCaseEditClose(fileName)}
              style={{
                padding: '4px 12px',
                fontSize: '11px',
                border: '1px solid #dee2e6',
                borderRadius: '4px',
                backgroundColor: 'white',
                cursor: 'pointer'
              }}
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              style={{
                padding: '4px 12px',
                fontSize: '11px',
                border: '1px solid #183FCE',
                borderRadius: '4px',
                backgroundColor: '#183FCE',
                color: 'white',
                cursor: 'pointer'
              }}
            >
              Save
            </button>
          </div>
        </div>
      </DropdownPortal>
    );
  };

  const fileColumns = [
    {
      accessorKey: "name",
      header: () => <span style={{ color: "#183FCE", fontSize: '12px', fontWeight: 'bold' }}>File Name</span>,
      width: "90%",
      cell: ({ row }: { row: any }) => {

        console.log(row);
       return <div style={{ 
          maxWidth: '80%', 
          overflow: 'hidden', 
          textOverflow: 'ellipsis', 
          whiteSpace: 'wrap',
          fontSize: '0.875rem',
          fontFamily: 'Roboto'
        }} title={row.original.name}>
          {row.original.name}
          <div className="d-flex gap-1 mt-2 align-items-center">
            {row.original.use_cases.map((use_case: any) => {
              // Find the use case name from the useCases array
              const useCaseObj = useCases.find(uc => uc.value === Number(use_case.split('_')[2]));
              const useCaseName = useCaseObj ? useCaseObj.name : `use_case_${use_case}`;
              
              return (
                <span key={use_case} style={{
                  backgroundColor: '#A7CCFF',
                  padding: '4px 9px',
                  borderRadius: '0.75rem',
                  fontSize: '0.75rem'
                }}>{useCaseName}</span>
              );
            })}
            
            {/* Ellipses Icon for Use Case Edit */}
            <div
              ref={(el) => {
                if (!useCaseEditDropdownRefs.current[row.original.name]) {
                  useCaseEditDropdownRefs.current[row.original.name] = React.createRef();
                }
                if (useCaseEditDropdownRefs.current[row.original.name]) {
                  (useCaseEditDropdownRefs.current[row.original.name] as any).current = el;
                }
              }}
              style={{
                backgroundColor: '#A7CCFF',
                borderRadius: '0.75rem',
                width: '30px',
                height: '20px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                marginLeft: '4px'
              }}
              onClick={(e) => {
                e.stopPropagation();
                handleUseCaseEditToggle(row.original.name);
              }}
              title="Edit use cases"
            >
              <i className="bi bi-three-dots" style={{ color: 'white', fontSize: '1.5rem' }}></i>
            </div>
            
            <UseCaseEditDropdown 
              fileName={row.original.name} 
              currentUseCases={row.original.use_cases} 
            />
          </div>

          
        </div>
      },
    },
    {
      id: "actions",
      header: () => <span style={{ color: "#183FCE", fontSize: '12px', fontWeight: 'bold' }}>Actions</span>,
      width: "10%",
      cell: ({ row }: { row: any }) => {
        const fileName = row.original.name;
        const isProcessing = isFileBeingProcessed(fileName);
        const processId = getFileProcessId(fileName);
        
        // Check if file exists in uploaded files list
        const fileExistsInUploaded = uploadedFiles.some(uploadedFile => {
          const uploadedFileName = uploadedFile.split('/').pop();
          return uploadedFileName === fileName;
        });
        
        return (
          <div className="d-flex gap-2 align-items-center">
            {isProcessing ? (
              <button
                style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '4px',
                    borderRadius: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#183FCE'
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f0f0f0'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                onClick={(e) => {
                  e.stopPropagation();
                  if (processId) {
                    checkStatus(processId);
                  }
                }}
                title="Check Status"
              >
                <i className="bi bi-hourglass-split"></i>
              </button>
            ) : (
              <button
                style={{
                    background: 'none',
                    border: 'none',
                    cursor: fileExistsInUploaded ? 'pointer' : 'not-allowed',
                    padding: '4px',
                    borderRadius: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    opacity: fileExistsInUploaded ? 1 : 0.5,
                    color: fileExistsInUploaded ? 'inherit' : '#6c757d'
                }}
                onMouseEnter={(e) => {
                  if (fileExistsInUploaded) {
                    e.currentTarget.style.backgroundColor = '#f0f0f0';
                  }
                }}
                onMouseLeave={(e) => {
                  if (fileExistsInUploaded) {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  if (fileExistsInUploaded) {
                    handleDownloadFile(row.original.name);
                  }
                }}
                title={fileExistsInUploaded ? "Download" : "download disabled"}
                disabled={!fileExistsInUploaded}
              >
                <i className="bi bi-download"></i>
              </button>
            )}
            <button
              style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '4px',
                  borderRadius: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
              }}
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteFile(row.original.name);
              }}
              title="Delete"
            >
              <DeleteIcon />
            </button>
          </div>
        );
      },
    },
  ];

  if (!isOpen) return null;

  return (
    <>
      <div
        className="offcanvas-backdrop fade show"
        onClick={onClose}
      ></div>
      <div
        className="offcanvas offcanvas-end show"
        tabIndex={-1}
        style={{ 
          zIndex: 1050, 
          width: '90vw',
          maxWidth: '1400px',
          overflow: 'visible'
        }}
      >
        <div className="offcanvas-header" style={{backgroundColor: 'white', borderBottom: '1px solid #dee2e6'}}>
          <div className="d-flex align-items-center">
            <div 
              className="me-4"
              onClick={onSwitchToUpload}
              style={{
                cursor: 'pointer',
                fontSize: '1.25rem',
                fontWeight: 'normal',
                color: '#6c757d',
                padding: '10px 15px',
                // borderRadius: '8px',
                border: '0px solid #183FCE',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#f8f9fa';
                e.currentTarget.style.color = '#183FCE';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.color = '#6c757d';
              }}
            >
              <i className="bi bi-cloud-upload me-2"></i>
              UPLOAD FILES
            </div>
            <NextTabIcon style={{
                marginRight: '20px'
            }} />
            <div 
              className="me-4 ml-3"
              style={{
                cursor: 'pointer',
                fontSize: '1.25rem',
                fontWeight: 'bold',
                color: '#183FCE',
                padding: '10px 15px',
                // backgroundColor: '#f8f9ff',
                borderRadius: '8px',
                border: '0px solid #183FCE'
              }}
            >
              <i className="bi bi-robot me-2"></i>
              DATA INGESTION
            </div>
          </div>
          <button
            type="button"
            className="btn-close"
            onClick={onClose}
          ></button>
        </div>
        
        <div className="offcanvas-body p-0" style={{ overflow: 'visible' }}>
          <div className="row g-0" style={{ overflow: 'visible' }}>
            {/* Left Side - File Selection & Ingest Form */}
            <div className="col-md-5" style={{ overflow: 'visible' }}>
              <div className="p-4 min-vh-85 d-flex flex-column" style={{
                backgroundColor: '#f8f9fa',
                borderRadius: '1.875rem',
                border: '1px solid #dee2e6',
                margin: '1rem',
                overflow: 'visible'
              }}>
                <div className="text-start mb-3">                  
                  <h6 className="mb-0 small" style={{ fontWeight: 'bold', color: '#183FCE' }}>
                    Select files and configure bulk ingestion
                </h6>
                </div>

                {/* Quick File Selection Section */}
                {pendingFiles.length > 0 && (
                  <>
                    <div className="mb-3">
                      <div className="d-flex flex-column" style={{
                        maxHeight: '14vh',
                        overflowY: 'auto'
                      }}>
                        {pendingFiles.map((fileName, index) => (
                          <div 
                            key={index}
                            className="border rounded-0 p-2 bg-white"
                            style={{ 
                              borderBottom: '0px !important',
                              borderColor: selectedFiles.includes(fileName) ? '#183FCE' : '#dee2e6',
                              backgroundColor: selectedFiles.includes(fileName) ? '#f8f9ff' : 'white'
                            }}
                          >
                            <div className="d-flex align-items-center">
                              <div className="form-check me-2">
                                <input
                                  className="form-check-input"
                                  type="checkbox"
                                  id={`file-${index}`}
                                  checked={selectedFiles.includes(fileName)}
                                  onChange={() => handleFileSelect(fileName)}
                                />
                              </div>
                              <div 
                                className="d-flex align-items-center justify-content-center me-2"
                                style={{
                                  width: '20px',
                                  height: '20px',
                                  backgroundColor: '#f8f9fa',
                                  borderRadius: '4px'
                                }}
                              >
                                <i className="bi bi-file-earmark-text" style={{ color: '#183FCE', fontSize: '12px' }}></i>
                              </div>
                              <div className="flex-grow-1">
                                <div 
                                  className="fw-medium text-truncate" 
                                  style={{ maxWidth: '200px', fontSize: '11px' }}
                                  title={fileName.split('/')[2]}
                                >
                                  {fileName.split('/')[2]}
                                </div>
                              </div>
                              <DeleteIcon
                                onClick={() => handleDeleteUploadedFile(fileName)} 
                                style={{ 
                                  cursor: 'pointer',
                                  width: '14px',
                                  height: '14px'
                                }}/>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    <hr className="my-3" style={{ borderColor: '#dee2e6' }} />
                  </>
                )}

                {/* Bulk Ingest Form Section */}
                <div className="mb-2">
                  <h6 className="mb-2 small" style={{ fontWeight: 'bold', color: '#183FCE' }}>
                    Bulk Configuration
                  </h6>
                </div>

                <form onSubmit={handleSubmit} className="flex-grow-1" style={{ fontSize: '13px', overflow: 'visible' }}>
                  {/* File Selection */}

                  {/* Use Case Selection */}
                  <div className="row mb-2 align-items-center">
                    <div className="col-3">
                      <label htmlFor="floatingSelect" style={{ fontSize: '11px', marginBottom: 0 }}>Use Case</label>
                    </div>
                    <div className="col-9">
                      <div
                        ref={useCaseDropdownRef}
                        className="form-select use-case-dropdown bg-white position-relative"
                        onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                        style={{ fontSize: '13px', cursor: 'pointer', height: '40px', display: 'flex', alignItems: 'center' }}
                      >
                        <div className="text-start">
                          {formData.use_case.length > 0
                            ? `${formData.use_case.length} use cases selected`
                            : "Select use cases"}
                        </div>
                      </div>
                      {errors.use_case && (
                        <div className="text-danger small">{errors.use_case}</div>
                      )}
                    </div>
                  </div>

                  <DropdownPortal
                    isOpen={isDropdownOpen}
                    onClose={() => setIsDropdownOpen(false)}
                    triggerRef={useCaseDropdownRef}
                  >
                    <div style={{ padding: '8px 16px', fontWeight: 'bold', borderBottom: '1px solid #dee2e6' }}>
                      Select use cases
                    </div>
                    {useCases.map((useCase: UseCase, key) => (
                      <div
                        key={`use_case_options_${key}`}
                        style={{ padding: '8px 16px', cursor: 'pointer' }}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleUseCaseChange(e, useCase.value.toString());
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                      >
                        <label className="d-flex align-items-center gap-2" style={{ cursor: 'pointer', marginBottom: 0 }}>
                          <input
                            type="checkbox"
                            checked={formData.use_case.includes(useCase.value.toString())}
                            onChange={(e) => {
                              e.stopPropagation();
                              handleUseCaseChange(e, useCase.value.toString());
                            }}
                          />
                          {useCase.name}
                        </label>
                      </div>
                    ))}
                  </DropdownPortal>

                 

                  {/* Frameworks Selection */}
                  <div className="row mb-2 align-items-center">
                    <div className="col-3">
                      <label htmlFor="frameworks" style={{ fontSize: '11px', marginBottom: 0 }}>Frameworks</label>
                    </div>
                    <div className="col-9">
                      <div
                        ref={frameworkDropdownRef}
                        className="form-select framework-dropdown bg-white position-relative"
                        onClick={() => setIsFrameworkDropdownOpen(!isFrameworkDropdownOpen)}
                        style={{ fontSize: '13px', cursor: 'pointer', height: '40px', display: 'flex', alignItems: 'center' }}
                      >
                        <div className="text-start">
                          {formData.frameworks.length > 0
                            ? `${formData.frameworks.length} Frameworks selected`
                            : "Select Frameworks"}
                        </div>
                      </div>
                      {errors.frameworks && (
                        <div className="text-danger small">{errors.frameworks}</div>
                      )}
                    </div>
                  </div>

                  <DropdownPortal
                    isOpen={isFrameworkDropdownOpen}
                    onClose={() => setIsFrameworkDropdownOpen(false)}
                    triggerRef={frameworkDropdownRef}
                  >
                    <div style={{ padding: '8px 16px', fontWeight: 'bold', borderBottom: '1px solid #dee2e6' }}>
                      Select Frameworks
                    </div>
                    {frameworks.map((framework: FrameworkCategory, key) => (
                      <div
                        key={`framework_options_${key}`}
                        style={{ padding: '8px 16px', cursor: 'pointer' }}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleFrameworkChange(e, framework.id.toString());
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                      >
                        <label className="d-flex align-items-center gap-2" style={{ cursor: 'pointer', marginBottom: 0 }}>
                          <input
                            type="checkbox"
                            checked={formData.frameworks.includes(framework.id.toString())}
                            onChange={(e) => {
                              e.stopPropagation();
                              handleFrameworkChange(e, framework.id.toString());
                            }}
                          />
                          {framework.title} 
                          <span className="text-secondary">
                            {!framework.is_enabled && " - Disabled"}
                          </span>
                        </label>
                      </div>
                    ))}
                  </DropdownPortal>

                {/* Version */}
                <div className="row mb-2 align-items-center">
                  <div className="col-3">
                    <label htmlFor="version" style={{ fontSize: '11px', marginBottom: 0 }}>Version</label>
                  </div>
                  <div className="col-9">
                    <input
                      type="text"
                      className={`form-control bg-white ${errors?.version ? "is-invalid" : ""}`}
                      id="version"
                      placeholder=""
                      name="version"
                      onChange={handleChange}
                      value={formData.version}
                      style={{ fontSize: '13px', height: '40px' }}
                    />
                    {errors?.version && (
                      <div className="text-danger small">{errors?.version}</div>
                    )}
                  </div>
                </div>
                  {/* Custom Topics */}
                  <div className="row mb-2 align-items-center">
                    <div className="col-3">
                      <label htmlFor="custom_topics" style={{ fontSize: '11px', marginBottom: 0 }}>Custom Topics</label>
                    </div>
                    <div className="col-9">
                      <input
                        type="text"
                        className={`form-control bg-white ${errors?.custom_topics ? "is-invalid" : ""}`}
                        id="custom_topics"
                        placeholder="comma separated"
                        name="custom_topics"
                        onChange={handleChange}
                        value={formData.custom_topics}
                        style={{ fontSize: '13px', height: '40px' }}
                      />
                      {errors?.custom_topics && (
                        <div className="text-danger small">{errors?.custom_topics}</div>
                      )}
                    </div>
                  </div>

                  {/* Requires Enrichment */}
                  <div className="row mb-2 align-items-center">
                    <div className="col-3">
                      <label className="small" style={{ fontSize: '11px', marginBottom: 0 }}>Enrichment</label>
                    </div>
                    <div className="col-9">
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          name="requires_enrichment"
                          id="requires_enrichment"
                          checked={formData.requires_enrichment}
                          onChange={(e) => setFormData({ ...formData, requires_enrichment: e.target.checked })}
                        />
                        <label className="form-check-label small" htmlFor="requires_enrichment" style={{ fontSize: '11px' }}>
                          Requires Enrichment
                        </label>
                      </div>
                    </div>
                  </div>

                  {/* Submit Button */}
                  <div className="mt-5 d-flex justify-content-end ">
                    <button
                      type="submit"
                      className="btn w-50"
                      disabled={Object.keys(errors).length > 0 || ingestLoading || formData.use_case.length === 0 || selectedFiles.length === 0}
                      style={{
                        backgroundColor: (Object.keys(errors).length > 0 || ingestLoading || formData.use_case.length === 0 || selectedFiles.length === 0) ? '#6c757d' : '#183FCE',
                        color: 'white',
                        border: 'none',
                        fontSize: '13px',
                        fontWeight: 'bold',
                        height: '40px',
                        cursor: (Object.keys(errors).length > 0 || ingestLoading || formData.use_case.length === 0 || selectedFiles.length === 0) ? 'not-allowed' : 'pointer'
                      }}
                    >
                      {ingestLoading ? (
                        <>
                          <i className="bi bi-hourglass-split me-2"></i>
                          Processing...
                        </>
                      ) : (
                        <>
                          <i className="bi bi-robot me-2"></i>
                          Start Ingestion
                        </>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>

            {/* Right Side - File List */}
            <div className="col-md-7">
              <div className="p-4 h-100 overflow-auto">
                <div className="d-flex justify-content-between align-items-center mb-3 mt-4">
                  <h6 className="mb-0" style={{ fontWeight: 'bold', color: '#183FCE' }}>
                    Ingested Files
                  </h6>
                  <button 
                    className="btn btn-sm btn-outline-primary"
                    onClick={getFilesList}
                    disabled={loading}
                  >
                    <i className="bi bi-arrow-clockwise"></i> Refresh
                  </button>
                </div>

                {/* Search and File Type Filter */}
                <div className='d-flex justify-content-between align-items-center mb-3'>
                  <div className="position-relative" style={{ minWidth: '300px' }}>
                    <input
                      type="text"
                      className="form-control bg-white"
                      placeholder="Search here"
                      value={searchTerm}
                      onChange={handleSearchChange}
                      style={{
                        borderRadius: '20px',
                        paddingLeft: '20px',
                        fontSize: '14px'
                      }}
                    />
                    <i 
                      className="bi bi-search position-absolute"
                      style={{
                        right: '12px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        color: '#26397E',
                        stroke: '#26397E',
                        fontWeight: 'bolder',
                      }}
                    ></i>
                  </div>
                  <div>
                    {/* <label className="small mb-2 px-2" style={{ fontWeight: 'bold', color: '#183FCE' }}>
                      Filter by File Type:
                    </label> */}
                    <div className="btn-group" role="group" aria-label="File type filter">
                      {['ALL', 'CSV', 'XLSX', 'WORD', 'PDF'].map((filter) => (
                        <button
                          key={filter}
                          type="button"
                          className={`btn ${selectedFilter === filter ? 'btn-primary' : 'btn-outline-secondary'}`}
                          onClick={() => handleFilterClick(filter)}
                          style={{
                            backgroundColor: selectedFilter === filter ? '#183FCE' : '#f8f9fa',
                            color: selectedFilter === filter ? 'white' : 'black',
                            borderColor: '#dee2e6',
                            fontSize: '14px',
                            fontWeight: '500',
                            transition: 'all 0.2s ease',
                            minWidth: '60px'
                          }}
                          onMouseEnter={(e) => {
                            if (selectedFilter !== filter) {
                              e.currentTarget.style.backgroundColor = '#e9ecef';
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (selectedFilter !== filter) {
                              e.currentTarget.style.backgroundColor = '#f8f9fa';
                            }
                          }}
                        >
                          {filter === 'ALL' ? 'All' : filter}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
                
                <div className="ingested-files-table" style={{ height: 'calc(100vh - 200px)', width: '100%', overflow: 'none' }}>
                  <AdminTables
                    data={tableData}
                    columns={fileColumns}
                    rowSelectionEnabled={false}
                    onRowSelect={() => {}}
                    showSearch={false}
                    isLoading={loading}
                    containerStyle={{
                      width: "100%",
                      height: "90%",
                      overflowY: "auto"
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Alert */}
      {alert && (
        <Alert
          message={alert.message}
          type={alert.type}
          show={alert.show}
          onClose={() => setAlert(null)}
        />
      )}
    </>
  );
};

export default IngestedFilesSidebar; 